// The survey's own details, as a section-page form: name, status, language
// (General), and template + thank-you copy (Appearance). Same schema and the
// same three Field blocks the old Settings tab's form used, split into two
// smaller forms so each can save (and follow the preview) on its own.

import { Input, PropertyPanel, SegmentedControl, Select, Textarea } from "@pej/kore/components";
import { z } from "zod";
import { useTemplates } from "@/api/templates";
import type { Survey } from "@/api/types";
import { Field, useFormContext } from "@/lib/forms";
import { DEFAULT_LANGUAGE, LANGUAGES, languageLabel } from "./languages";

// Sentinel for "no template chosen" (surveys.new.tsx's own convention) —
// stripped back to `null` in the section's `pick`, which is what clears the
// column.
const NO_TEMPLATE = "";

export const surveyDetailsSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
    templateId: z.string(),
    thankYouText: z.string(),
    displayTitle: z.string(),
    displayDescription: z.string(),
    language: z.string(),
    active: z.boolean(),
});

export type SurveyDetailsValues = z.infer<typeof surveyDetailsSchema>;

export function toDetailsDefaults(
    survey: Pick<
        Survey,
        "name" | "language" | "active" | "templateId" | "thankYouText" | "displayTitle" | "displayDescription"
    >,
): SurveyDetailsValues {
    return {
        name: survey.name,
        language: survey.language || DEFAULT_LANGUAGE,
        active: survey.active,
        templateId: survey.templateId ?? NO_TEMPLATE,
        thankYouText: survey.thankYouText ?? "",
        displayTitle: survey.displayTitle ?? "",
        displayDescription: survey.displayDescription ?? "",
    };
}

/** Name, status (survey page only) and language. */
export function GeneralFields({ showStatus }: { showStatus: boolean }) {
    const { control } = useFormContext<SurveyDetailsValues>();
    return (
        <PropertyPanel>
            <Field name="name" control={control} label="Name" required render={({ field }) => <Input {...field} />} />
            {showStatus && (
                <Field
                    name="active"
                    control={control}
                    label="Status"
                    description="An inactive survey's placements stop accepting responses."
                    render={({ field: { value, onChange } }) => (
                        <SegmentedControl<"active" | "inactive">
                            aria-label="Status"
                            value={value ? "active" : "inactive"}
                            onValueChange={(next) => onChange(next === "active")}
                            options={[
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "Inactive" },
                            ]}
                        />
                    )}
                />
            )}
            <Field
                name="language"
                control={control}
                label="Language"
                description="Buttons and system messages. Your own wording above is never translated."
                render={({ field: { value, onChange, name } }) => (
                    <Select value={value} onValueChange={onChange} name={name}>
                        <Select.Trigger>
                            <Select.Value>{(v: unknown) => languageLabel(String(v))}</Select.Value>
                        </Select.Trigger>
                        <Select.Content>
                            {LANGUAGES.map((language) => (
                                <Select.Item key={language.code} value={language.code}>
                                    {language.label}
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select>
                )}
            />
        </PropertyPanel>
    );
}

/** The template the survey renders under. */
export function AppearanceFields({ workspaceId }: { workspaceId: string }) {
    const { control } = useFormContext<SurveyDetailsValues>();
    const templatesQuery = useTemplates(workspaceId);
    const templates = templatesQuery.data ?? [];
    return (
        <PropertyPanel>
            <Field
                name="templateId"
                control={control}
                label="Template"
                optional
                description="The shared look this survey renders under."
                render={({ field: { value, onChange, name } }) => (
                    <Select value={value} onValueChange={onChange} name={name}>
                        <Select.Trigger>
                            <Select.Value>
                                {(v: unknown) => templates.find((template) => template.id === v)?.name ?? "No template"}
                            </Select.Value>
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Item value={NO_TEMPLATE}>No template</Select.Item>
                            {templates.map((template) => (
                                <Select.Item key={template.id} value={template.id}>
                                    {template.name}
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select>
                )}
            />
        </PropertyPanel>
    );
}

/** The header guests see above every step. */
export function HeaderFields() {
    const { control } = useFormContext<SurveyDetailsValues>();
    return (
        <PropertyPanel>
            <Field
                name="displayTitle"
                control={control}
                label="Title"
                optional
                description="Shown to guests above every step. The survey's name stays internal."
                render={({ field }) => <Input {...field} placeholder="How was your visit?" />}
            />
            <Field
                name="displayDescription"
                control={control}
                label="Description"
                optional
                description="A line under the title."
                render={({ field }) => <Input {...field} placeholder="Two quick questions, under a minute." />}
            />
        </PropertyPanel>
    );
}

/** The message on the closing screen. */
export function ThankYouFields() {
    const { control } = useFormContext<SurveyDetailsValues>();
    return (
        <PropertyPanel>
            <Field
                name="thankYouText"
                control={control}
                label="Thank-you message"
                optional
                render={({ field }) => (
                    <Textarea {...field} placeholder="Thanks for your feedback!" aria-label="Thank-you message" />
                )}
            />
        </PropertyPanel>
    );
}
